import React, { useState, useRef, useEffect } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './ProfileModal.css';

/**
 * Profile Modal Component for updating user profile
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {function} props.onHide - Function to call when hiding the modal
 * @param {Object} props.userData - User data object containing profile information
 * @param {function} props.onUpdateProfile - Function to call when profile is updated
 */
const ProfileModal = ({ show, onHide, userData, onUpdateProfile }) => {
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // File input ref
  const fileInputRef = useRef(null);

  // Load profile data from backend when modal opens - using ready function from socrates.js
  useEffect(() => {
    if (show && window.UserUtilities && window.CheckSessionToken) {
      setIsLoading(true);
      const helper = new window.UserUtilities();
      const token = window.CheckSessionToken();
      
      // Call the ready function from socrates.js
      helper.getProfile(token, 
        (result, textStatus, xhr) => {
          // Success callback - same field names as old code
          console.log('Profile loaded:', result);
          setFirstName(result.Firstname || userData?.firstname || '');
          setLastName(result.Lastname || userData?.lastname || '');
          // Check multiple possible field names and fallback to userData
          const imageFromAPI = result.ImageURL || result.ProfileImage || result.imageUrl || result.image_url || '';
          const imageValue = (imageFromAPI && imageFromAPI.trim() !== '') 
            ? imageFromAPI 
            : (userData?.profileimage && userData.profileimage.trim() !== '' ? userData.profileimage : '');
          setProfileImage(imageValue);
          setIsLoading(false);
        },
        (textStatus, xhr) => {
          // Failure callback - same signature as socrates.js
          console.error('Failed to load profile:', textStatus, xhr);
          // Fallback to userData prop
          setFirstName(userData?.firstname || '');
          setLastName(userData?.lastname || '');
          // Only set profile image if it exists and is not empty
          setProfileImage(userData?.profileimage && userData.profileimage.trim() !== '' ? userData.profileimage : '');
          setIsLoading(false);
        }
      );
    } else if (show) {
      // Fallback if utilities not available
      setFirstName(userData?.firstname || '');
      setLastName(userData?.lastname || '');
      // Only set profile image if it exists and is not empty
      setProfileImage(userData?.profileimage && userData.profileimage.trim() !== '' ? userData.profileimage : '');
    }
  }, [show, userData]);

  // Validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = 'Please enter a first name';
    } else if (/[<>\\/"']/.test(firstName)) {
      newErrors.firstName = 'First name contains illegal characters';
    }
    
    if (!lastName.trim()) {
      newErrors.lastName = 'Please enter a last name';
    } else if (/[<>\\/"']/.test(lastName)) {
      newErrors.lastName = 'Last name contains illegal characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle image upload - using same method as old code (calls GetSignature from socrates.js pattern)
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file extension (same as old code)
    const fileName = file.name;
    const extStartIndex = fileName.lastIndexOf('.');
    const fileExt = fileName.substring(extStartIndex + 1).toLowerCase();
    const allowedExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'xbm', 'tif', 'pjp', 'jfif', 'webp', 'pjpeg', 'avif', 'ico', 'tiff', 'svgz', 'svg'];
    
    if (allowedExts.indexOf(fileExt) === -1) {
      setErrors({ ...errors, image: 'The chosen file is not supported. Please choose a normal image!' });
      return;
    }

    setIsUploading(true);
    
    // Call GetSignature from socrates.js pattern (same as old code)
    if (window.$ && window.$.ajax) {
      window.$.ajax({
        url: "webservices/Authentication.svc/GetSignature",
        type: 'POST',
        dataType: 'json',
        contentType: "application/json; charset=utf-8",
        data: JSON.stringify({ fileName: fileName }),
        xhrFields: { withCredentials: true },
        success: (retdata) => {
          // Validate response data
          if (!retdata || !retdata.key || !retdata.policy || !retdata.signature) {
            setErrors({ ...errors, image: 'Invalid response from server. Please try again.' });
            console.error('Invalid GetSignature response:', retdata);
            setIsUploading(false);
            return;
          }

          // AWSAccessKeyId is required for S3 upload - must be a non-empty string
          if (!retdata.AWSAccessKeyId || typeof retdata.AWSAccessKeyId !== 'string' || retdata.AWSAccessKeyId.trim() === '') {
            setErrors({ ...errors, image: 'AWS Access Key ID is missing or invalid from server response. Please contact support.' });
            console.error('AWSAccessKeyId not provided or invalid by GetSignature endpoint:', retdata);
            setIsUploading(false);
            return;
          }

          // Prepare form data for S3 upload
          const formData = new FormData();
          formData.append('key', retdata.key);
          formData.append('AWSAccessKeyId', retdata.AWSAccessKeyId);
          formData.append('acl', 'private');
          formData.append('success_action_status', '200');
          formData.append('policy', retdata.policy);
          formData.append('signature', retdata.signature);
          formData.append('file', file);

          // Debug: Verify AWSAccessKeyId is in formData
          console.log('FormData AWSAccessKeyId:', formData.get('AWSAccessKeyId'));
          console.log('Uploading with key:', retdata.key, 'AWSAccessKeyId:', retdata.AWSAccessKeyId);

          // Upload to S3 (same URL as old code)
          const fileUploadUrl = window.fileUploadUrl || 'https://learningants.s3.amazonaws.com';
          
          // Upload to S3 using fetch. Use credentials: 'omit' so CORS works:
          // S3 often returns Access-Control-Allow-Origin: *; with credentials that is forbidden.
          // Auth for this request is the policy/signature in the form, not cookies.
          fetch(fileUploadUrl, {
            method: 'POST',
            body: formData,
            credentials: 'omit',
          })
          .then(response => {
            if (!response.ok) {
              throw new Error('Failed to upload image to S3');
            }
            // Use the key as imageUrl (same as old code)
            setProfileImage(retdata.key);
            setErrors({ ...errors, image: null });
            setIsUploading(false);
          })
          .catch(error => {
            setErrors({ ...errors, image: 'Failed to upload image: ' + error.message });
            console.error('Image upload failed:', error);
            setIsUploading(false);
          });
        },
        error: (xhr, textStatus, errorThrown) => {
          setErrors({ ...errors, image: 'Failed to get S3 signature: ' + errorThrown });
          console.error('Failed to get S3 signature:', errorThrown);
          setIsUploading(false);
        }
      });
    } else {
      // Fallback if jQuery not available
      setErrors({ ...errors, image: 'jQuery is required for file upload' });
      setIsUploading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const helper = new window.UserUtilities();
      const token = getTokenForRequest();
      
      await helper.updateProfile(
        firstName,
        lastName,
        profileImage,
        token,
        '',
        () => {
          // Update cookies
          const cookieHelper = new window.CookieUtilities();
          cookieHelper.setCookie('firstname', firstName);
          cookieHelper.setCookie('lastname', lastName);
          cookieHelper.setCookie('profileimage', profileImage);
          
          // Call update callback
          onUpdateProfile({
            firstname: firstName,
            lastname: lastName,
            profileimage: profileImage
          });
          
          // Close modal
          onHide();
          
          // Show success message
          window.modalMessage('Success', 'Profile updated successfully');
        },
        (status) => {
          if (status.status === 403) {
            setErrors({ ...errors, submit: 'Invalid input provided' });
          } else {
            setErrors({ ...errors, submit: 'Failed to update profile' });
          }
        }
      );
    } catch (error) {
      setErrors({ ...errors, submit: 'An error occurred while updating profile' });
      console.error('Profile update failed:', error);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered profile-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Update Profile</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onHide}
                aria-label="Close"
              />
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="profile-image-container">
                      {isLoading ? (
                        <div className="profile-image-preview d-flex align-items-center justify-content-center">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="profile-image-preview"
                          style={{
                            backgroundImage: profileImage && profileImage.trim() !== '' 
                              ? `url(${profileImage.startsWith('http') 
                                  ? profileImage 
                                  : `https://d6adgef862n1y.cloudfront.net/${encodeURIComponent(profileImage)}`})`
                              : 'url(css/images/default_profile.png)',
                            backgroundSize: '100%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            backgroundColor: '#EFEFEF'
                          }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="d-none"
                            onChange={handleImageUpload}
                          />
                          {isUploading && (
                            <div className="upload-overlay">
                              <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">Uploading...</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {errors.image && (
                        <div className="text-danger mt-2 small">{errors.image}</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label htmlFor="firstName" className="form-label">First Name</label>
                      <input
                        type="text"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                      {errors.firstName && (
                        <div className="invalid-feedback">{errors.firstName}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="lastName" className="form-label">Last Name</label>
                      <input
                        type="text"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                      {errors.lastName && (
                        <div className="invalid-feedback">{errors.lastName}</div>
                      )}
                    </div>

                    {errors.submit && (
                      <div className="alert alert-danger">{errors.submit}</div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onHide}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
};

export default ProfileModal; 